/**
 * Past Winner Component
 *
 * Strict TypeScript conversion for the past winner card interactions.
 */

type PastWinnerContainer = HTMLElement & {
  dataset: DOMStringMap & {
    pifsterPastWinnerInit?: string;
    instanceId?: string;
  };
};

type WordPressWindow = Window & {
  wp?: {
    readonly domReady?: (callback: () => void) => void;
  };
};

type PastWinnerPlayButton = HTMLElement & {
  dataset: DOMStringMap & {
    video?: string;
    title?: string;
  };
};

function syncDescriptionScrollState(content: HTMLElement): void {
  const hasOverflow = content.scrollHeight > content.clientHeight + 1;
  const isAtEnd = !hasOverflow || content.scrollTop + content.clientHeight >= content.scrollHeight - 2;

  content.classList.toggle('is-scrollable', hasOverflow);
  content.classList.toggle('is-scrolled-end', isAtEnd);
}

function extractYouTubeId(url: string): string | null {
  const normalized = url.trim();
  if (normalized === '') {
    return null;
  }

  const patterns = [
    /youtu\.be\/([^\/\?#&]+)/i,
    /youtube\.com\/watch\?(?:[^&]+&)*v=([^&#]+)/i,
    /youtube\.com\/embed\/([^\/\?#&]+)/i,
    /youtube\.com\/v\/([^\/\?#&]+)/i,
  ];

  for (const pattern of patterns) {
    const match = normalized.match(pattern);
    if (match?.[1]) {
      return match[1];
    }
  }

  if (/^[a-zA-Z0-9_-]{11}$/.test(normalized)) {
    return normalized;
  }

  return null;
}

function closeYouTubeModal(modal: HTMLElement): void {
  const iframeContainer = modal.querySelector<HTMLElement>('.pifster-youtube-iframe-container');
  if (iframeContainer) {
    iframeContainer.innerHTML = '';
  }

  const titleElem = modal.querySelector<HTMLElement>('#pifster-youtube-modal-title');
  if (titleElem) {
    titleElem.textContent = '';
  }

  modal.classList.remove('open');
  document.body.classList.remove('pifster-modal-open');
}

function openYouTubeModal(title: string, videoUrl: string): void {
  const modal = document.getElementById('pifster-youtube-modal');
  if (!modal) {
    throw new Error('YouTube modal element not found. Ensure pifster-youtube-modal is rendered in the footer.');
  }

  const iframeContainer = modal.querySelector<HTMLElement>('.pifster-youtube-iframe-container');
  const titleElem = modal.querySelector<HTMLElement>('#pifster-youtube-modal-title');
  if (!iframeContainer) {
    throw new Error('YouTube iframe container not found in modal.');
  }

  const videoId = extractYouTubeId(videoUrl);
  if (!videoId) {
    throw new Error(`Could not extract a YouTube video ID from URL: ${videoUrl}`);
  }

  iframeContainer.innerHTML = '';
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
  iframe.allow = 'autoplay; encrypted-media';
  iframe.allowFullscreen = true;
  iframe.style.width = '100%';
  iframe.style.height = '100%';
  iframe.className = 'pifster-youtube-iframe';
  iframe.title = title;
  iframeContainer.appendChild(iframe);

  if (titleElem) {
    titleElem.textContent = title;
    titleElem.style.display = title ? 'block' : 'none';
  }

  document.body.classList.add('pifster-modal-open');
  modal.classList.add('open');

  const closeBtn = modal.querySelector<HTMLElement>('#pifster-youtube-modal-close');
  closeBtn?.focus();
}

function initPastWinnerComponents(): void {
  document.querySelectorAll<HTMLElement>('.past-winner-card[data-instance-id]').forEach((card) => {
    const container = card as PastWinnerContainer;
    const instanceId = String(container.dataset['instanceId'] ?? '').trim();
    if (!instanceId) {
      return;
    }

    if (container.dataset['pifsterPastWinnerInit'] === '1') {
      return;
    }
    container.dataset['pifsterPastWinnerInit'] = '1';

    const descriptionContent = container.querySelector<HTMLElement>('.past-winner-description-content');
    descriptionContent?.addEventListener('scroll', () => {
      syncDescriptionScrollState(descriptionContent);
    }, { passive: true });

    container.addEventListener('click', (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      const playButton = target.closest('.past-winner-play-launch, .past-winner-btn.donation');
      if (playButton instanceof HTMLElement && 'video' in playButton.dataset && playButton.dataset['video']) {
        event.preventDefault();
        const title = playButton.dataset['title'] || 'Video';
        openYouTubeModal(title, playButton.dataset['video']);
      }
    });

    container.addEventListener('click', (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      const toggle = target.closest('.past-winner-description-toggle');
      if (!(toggle instanceof HTMLElement)) {
        return;
      }

      event.preventDefault();
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', (!expanded).toString());

      const content = toggle.nextElementSibling;
      const arrow = toggle.querySelector<HTMLElement>('.past-winner-turn-arrow');

      if (content instanceof HTMLElement) {
        const isOpening = !expanded;
        content.classList.toggle('open', isOpening);

        if (isOpening) {
          content.setAttribute('tabindex', '0');
          content.scrollTop = 0;
          window.requestAnimationFrame(() => {
            syncDescriptionScrollState(content);
          });
        } else {
          content.removeAttribute('tabindex');
          content.classList.remove('is-scrollable', 'is-scrolled-end');
        }
      }

      if (arrow) {
        arrow.style.transform = expanded ? '' : 'rotate(90deg)';
      }
    });
  });
}

function bindBreakdanceImpactPopupLifecycle(): void {
  document.querySelectorAll<HTMLElement>('.bde-popup').forEach((popup) => {
    const iframe = popup.querySelector<HTMLIFrameElement>('.pifster-recent-winner-impact-video__iframe');
    const originalSrc = iframe?.getAttribute('src') ?? '';
    if (!iframe || originalSrc === '' || popup.dataset['pifsterImpactPopupInit'] === '1') {
      return;
    }

    popup.dataset['pifsterImpactPopupInit'] = '1';
    popup.addEventListener('breakdance_popup_open', () => {
      if (iframe.getAttribute('src') !== originalSrc) {
        iframe.setAttribute('src', originalSrc);
      }
    });
    popup.addEventListener('breakdance_popup_close', () => {
      iframe.setAttribute('src', 'about:blank');
    });
  });
}

function bindGlobalListeners(): void {
  document.body.addEventListener('click', (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const closeButton = target.closest('#pifster-youtube-modal-close');
    const overlay = target.closest('#pifster-youtube-modal-overlay');
    if (!closeButton && !overlay) {
      return;
    }

    const modal = document.getElementById('pifster-youtube-modal');
    if (modal) {
      closeYouTubeModal(modal);
    }
  });

  document.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key !== 'Escape') {
      return;
    }

    const modal = document.getElementById('pifster-youtube-modal');
    if (modal && modal.classList.contains('open')) {
      closeYouTubeModal(modal);
    }
  });
}

function boot(): void {
  bindBreakdanceImpactPopupLifecycle();
  bindGlobalListeners();
  initPastWinnerComponents();

  const wordPressWindow = window as WordPressWindow;
  if (typeof wordPressWindow.wp?.domReady === 'function') {
    wordPressWindow.wp.domReady(initPastWinnerComponents);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

export {};
